import React, { useState, useMemo } from "react";
import { Button } from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { GoalCard } from "./GoalCard";
import {
  AddGoalFormWrapper,
  PageTitle,
  Subtitle,
  NoGoalCard,
  BackButtonWrapper,
  Goals,
  AddGoalButton,
} from "./styles";
import { PlusRoundCircle, GrayTarget } from "../../Assets/SvgIcons";
import { Back } from "../../Components/Back/Back";
import CreateOrEditGoalModal from "../../Components/Modal/CreateOrEditGoalModal";
import { useSpark } from "../../hooks/useSpark";
import FullPageNexusLogoAnimation from "../../Reusable/FullPageNexusLogoAnimation/FullPageNexusLogoAnimation";
import { useOrganization } from "../../hooks/useOrganization";
import { customConsole } from "../../utils/helper";
import { useTierLimits, FREE_TIER_LIMITS } from "../../hooks/useTierLimits";
import toast from "react-hot-toast";

const GoalsManagement = () => {
  const { organizationId: organizationIdParam, sparkName } = useParams();
  const [modalVisible, setModalVisible] = useState(false);
  const [goalToEdit, setGoalToEdit] = useState<
    | {
        id: string;
        name: string;
        description: string;
      }
    | undefined
  >(undefined);

  const navigate = useNavigate();
  const tierInfo = useTierLimits();

  const { currentOrganization, currentOrganizationLoading, isCurrentUserAdmin } =
    useOrganization();

  const { goalsBySparkName: filteredGoals } = useOrganization();
  const { spark, sparksLoading } = useSpark();
  
  const maxGoals = tierInfo.isPaid ? Infinity : 3;
  const canAddGoal = filteredGoals.length < maxGoals;

  customConsole("log", "Spark", spark);

  const handleCreateGoal = () => {
    if (!canAddGoal && !tierInfo.isPaid) {
      toast.error(
        `Free tier limit: Maximum ${FREE_TIER_LIMITS.MAX_SPARKS} goals per Spark. Upgrade to paid tier for unlimited goals.`,
        { duration: 5000 }
      );
      return;
    }
    setGoalToEdit(undefined);
    setModalVisible(true);
  };

  if (!organizationIdParam || !sparkName) {
    return <p>"You removed a resource in the URL"</p>;
  }

  if (sparksLoading || currentOrganizationLoading)
    return <FullPageNexusLogoAnimation />;

  console.log("IS CURRENT USER ADMIN", isCurrentUserAdmin)

  return (
    <AddGoalFormWrapper>
      <BackButtonWrapper>
        <Back />
      </BackButtonWrapper>
      <PageTitle>{sparkName}</PageTitle>
      {isCurrentUserAdmin && (
        <Subtitle>
          {tierInfo.isPaid ? (
            <>Unlimited goals per spark and unlimited metrics per goal (Paid tier)</>
          ) : (
            <>Free tier: Up to {FREE_TIER_LIMITS.MAX_SPARKS} goals per spark and {FREE_TIER_LIMITS.MAX_METRICS_PER_SPARK} metrics per goal. Upgrade to paid ($19/month) for unlimited.</>
          )}
        </Subtitle>
      )}
      {filteredGoals.length === 0 && !isCurrentUserAdmin && (
        <p>No goals were added yet.</p>
      )}
      {filteredGoals.length === 0 && isCurrentUserAdmin && (
        <NoGoalCard>
          <GrayTarget />
          <p>You don't have any goals</p>
          <Button
            onClick={() => setModalVisible(true)}
            colorScheme="orange"
          >
            Create a new goal
          </Button>
        </NoGoalCard>
      )}

      {filteredGoals.length > 0 && currentOrganization?.id && (
        <Goals>
          {filteredGoals.map((goal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              organizationId={currentOrganization?.id}
              isAdmin={isCurrentUserAdmin}
              sparkId={spark?.id}
              isBlackFundGrantee={
                currentOrganization?.orgFields?.isBlackFundGrantee === true
              }
              onEdit={() => {
                setGoalToEdit({
                  name: goal.name,
                  description: goal.description,
                  id: goal.id,
                });
                setModalVisible(true);
              }}
            />
          ))}
          {isCurrentUserAdmin && canAddGoal && (
            <div className="button-row">
              <AddGoalButton onClick={handleCreateGoal}>
                <PlusRoundCircle color="black" />
                <span className="goal-button-text">Add new goal</span>
              </AddGoalButton>
            </div>
          )}
          {isCurrentUserAdmin && !canAddGoal && !tierInfo.isPaid && (
            <div className="button-row">
              <div style={{ 
                padding: "16px", 
                background: "#FEF3C7", 
                border: "1px solid #FCD34D", 
                borderRadius: "8px",
                textAlign: "center"
              }}>
                <p style={{ margin: 0, color: "#92400E", fontWeight: "500" }}>
                  ⚠️ Free tier limit reached: {FREE_TIER_LIMITS.MAX_SPARKS} goals per Spark
                </p>
                <p style={{ margin: "8px 0 0 0", fontSize: "14px", color: "#78350F" }}>
                  Upgrade to paid tier ($19/month) for unlimited goals and metrics
                </p>
              </div>
            </div>
          )}
        </Goals>
      )}

      {modalVisible && currentOrganization?.id && (
        <CreateOrEditGoalModal
          goalId={goalToEdit?.id}
          goalName={goalToEdit?.name}
          goalDescription={goalToEdit?.description}
          onClose={() => setModalVisible(false)}
          organizationId={currentOrganization?.id}
          spark={spark}
          isOpen={modalVisible}
        />
      )}
    </AddGoalFormWrapper>
  );
};

export default GoalsManagement;
